import { useMemo, useState, useCallback } from 'react';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import I18n from 'I18n';
import { AggregationTypes } from 'reporting-snowflake/relational/schema/column-records';
import { SnowflakeReportUpdateActions } from 'reporting-snowflake/relational/updaters/report/use-snowflake-updater';
import { getFilteredAggregationOptions, isMagicMeasure, setColumnAggregation } from 'reporting-snowflake/relational/utils/column-utils';
import { getCurrentBreakdownColumn } from '../../breakdown-editor-dropdown/utils';
import MetricEditor from '../components/MetricEditor';
import { DEFAULT_MAX_METRICS_COUNT } from '../constants';
import { useRelationalReportData, useRelationalReportInfrastructure, useRelationalReportProperties, useRelationalReportAdvancedProperties } from '../../../contexts/RelationalReportContext';
import useSRVSnowflakeUpdater from '../../../hooks/relationalReports/useSRVSnowflakeUpdater';
import { createColumnWithValidAggregation, getIsStagedColumn, getColumnValue } from '../../../lib/relational-report-utils/column-utils';
import { getExpressionField } from '../../../lib/relational-report-utils/expression-utils';
import { getNewColumnFromPropertyValue, getCurrentColumn, getIsFormulaField, getPropertyValue } from '../../../lib/relational-report-utils/property-utils';
import { getAddMetricColumnAndChannel } from '../../../lib/relational-report-utils/snowflake-updater-utils';
import ErrorAlert from '../../shared-components/ErrorAlert';
import { jsx as _jsx } from "react/jsx-runtime";
const RelationalReportMetricsEditorDropdown = props => {
  const {
    report,
    hideAddButton,
    onChange,
    maxMetricsCount = DEFAULT_MAX_METRICS_COUNT
  } = props;
  const {
    relationalReport,
    reportColumns: {
      currentMeasureColumns,
      allAvailableColumns,
      allStagedColumns
    },
    isLoading
  } = useRelationalReportData();
  const {
    snowflakeUpdater,
    fullTableAndPropertyMeta: {
      data: tableAndPropertyMeta,
      error: fullTableAndPropertyMetaError
    }
  } = useRelationalReportInfrastructure();
  const {
    availablePropertiesOptions
  } = useRelationalReportProperties();
  const {
    availableDefaultExpressions,
    availableDefaultPropertyData,
    allDatasetFields,
    formulaFieldExpressions
  } = useRelationalReportAdvancedProperties();
  const {
    addColumnToChannel
  } = useSRVSnowflakeUpdater(report);
  const [error, setError] = useState(null);
  const metricAliases = useMemo(() => {
    return currentMeasureColumns.map(metric => metric.alias).toList();
  }, [currentMeasureColumns]);
  const hasBreakdownValue = useMemo(() => {
    const currentBreakdownColumn = getCurrentBreakdownColumn(relationalReport);
    return !!currentBreakdownColumn;
  }, [relationalReport]);
  const reportMetrics = useMemo(() => {
    return currentMeasureColumns.map(column => {
      const property = getPropertyValue(column);
      return {
        property,
        aggregation: column.aggregation
      };
    }).toJS();
  }, [currentMeasureColumns]);
  const handleOnChange = useCallback(updatedReportDefinition => {
    const updatedReport = report.set('reportDefinition', updatedReportDefinition.report.toMap());
    onChange(updatedReport);
  }, [onChange, report]);
  const handleAddMetric = useCallback(() => {
    const metricData = getAddMetricColumnAndChannel(metricAliases, relationalReport, allAvailableColumns);
    const {
      error: addMetricError,
      updatedReport
    } = addColumnToChannel(metricData.column, metricData.channel);
    if (addMetricError) {
      setError(new Error(addMetricError));
      return;
    }
    if (updatedReport) {
      onChange(updatedReport);
    }
  }, [addColumnToChannel, metricAliases, relationalReport, allAvailableColumns, onChange]);
  const handleDeleteMetric = useCallback(index => {
    const metricAlias = metricAliases.get(index);
    if (!metricAlias || !tableAndPropertyMeta) {
      return;
    }
    let updatedReportDefinition = null;
    const originalColumn = relationalReport.columns.get(metricAlias);
    const propertyValue = getColumnValue(originalColumn);
    const isStagedColumn = getIsStagedColumn(propertyValue, allStagedColumns);
    if (!isStagedColumn) {
      const isFormulaField = getIsFormulaField(propertyValue, formulaFieldExpressions);
      const newColumnForStagedColumns = isFormulaField ? originalColumn : getNewColumnFromPropertyValue(propertyValue, tableAndPropertyMeta, availableDefaultExpressions, availableDefaultPropertyData, relationalReport, allDatasetFields);
      updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.MOVE_COLUMN_TO_STAGED_COLUMNS, {
        originalColumn,
        newColumnForStagedColumns
      });
    } else {
      updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.REMOVE_COLUMN_FROM_REPORT_COLUMNS, {
        column: originalColumn
      });
    }
    if (!updatedReportDefinition.error) {
      handleOnChange(updatedReportDefinition);
    }
  }, [allStagedColumns, allDatasetFields, availableDefaultExpressions, availableDefaultPropertyData, formulaFieldExpressions, tableAndPropertyMeta, metricAliases, handleOnChange, relationalReport, snowflakeUpdater]);
  const handleMetricChange = useCallback((index, updatedMetric) => {
    const metricAlias = metricAliases.get(index);
    if (!metricAlias || !tableAndPropertyMeta) {
      return;
    }
    const currentMetric = reportMetrics[index];
    const currentColumn = getCurrentColumn(relationalReport, metricAlias);
    const newProperty = updatedMetric.property;
    let updatedReportDefinition = null;
    if (currentMetric.property !== newProperty) {
      // Updated the metric in the report

      const originalColumn = relationalReport.columns.get(metricAlias);
      const originalColumnValue = getColumnValue(originalColumn);
      const isStagedColumn = getIsStagedColumn(originalColumnValue, allStagedColumns);
      const newColumn = createColumnWithValidAggregation({
        allAvailableColumns,
        value: newProperty,
        metricAlias,
        relationalReport,
        tableAndPropertyMeta
      });
      const expressionField = getExpressionField({
        value: newProperty,
        defaultExpressions: availableDefaultExpressions,
        relationalReport
      });
      if (!isStagedColumn) {
        updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.UPDATE_ENCODED_COLUMN_WITH_EXPRESSION_ADD_STAGED, {
          alias: metricAlias,
          newColumn,
          originalColumn,
          isReplace: true,
          expressionField
        });
      } else {
        updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.UPDATE_ENCODED_COLUMN_WITH_EXPRESSION, {
          alias: metricAlias,
          column: newColumn,
          isReplace: true,
          expressionField
        });
      }
      if (updatedReportDefinition && updatedReportDefinition.error) {
        setError(new Error(updatedReportDefinition.error));
        return;
      }
    }
    const currentAggregationValue = currentColumn.aggregation;
    const newAggregationValue = updatedMetric === null || updatedMetric === void 0 ? void 0 : updatedMetric.aggregation;
    if (newAggregationValue && currentAggregationValue !== newAggregationValue) {
      // Updated the aggregation in the report
      const column = currentColumn.delete('transform').delete('limit').delete('sort');
      updatedReportDefinition = snowflakeUpdater(SnowflakeReportUpdateActions.UPDATE_ENCODED_COLUMN, {
        alias: metricAlias,
        column: setColumnAggregation(column, newAggregationValue),
        isReplace: false
      });
      if (updatedReportDefinition && updatedReportDefinition.error) {
        setError(new Error(updatedReportDefinition.error));
        return;
      }
    }
    if (updatedReportDefinition) {
      handleOnChange(updatedReportDefinition);
    }
  }, [allAvailableColumns, allStagedColumns, availableDefaultExpressions, tableAndPropertyMeta, metricAliases, handleOnChange, relationalReport, reportMetrics, snowflakeUpdater]);
  const getAggregationOptions = useCallback(index => {
    // Relational reports typically use a fixed set of aggregations
    const currentColumn = currentMeasureColumns.get(index);
    return getFilteredAggregationOptions(currentColumn, currentColumn.aggregation);
  }, [currentMeasureColumns]);
  const isDeleteDisabled = useCallback(__index => {
    return metricAliases.size <= 1; // Don't allow deleting the last metric
  }, [metricAliases]);
  const isAggregationDisabled = useCallback(index => {
    const currentColumn = currentMeasureColumns.get(index);
    const options = getAggregationOptions(index);
    return isMagicMeasure(currentColumn) || currentColumn.aggregation === AggregationTypes.DELEGATE || options.length <= 1;
  }, [currentMeasureColumns, getAggregationOptions]);
  const getAddMetricDisabledReason = useCallback(() => {
    if (metricAliases.size >= maxMetricsCount) {
      return I18n.text('report-customization-components.customization-components.metrics-editor-dropdown.config.tooltip.maxMetrics');
    }
    if (hasBreakdownValue) {
      return I18n.text('report-customization-components.customization-components.metrics-editor-dropdown.config.tooltip.breakdownPresent');
    }
    return undefined;
  }, [metricAliases.size, maxMetricsCount, hasBreakdownValue]);
  const getAggregationDisabledReason = useCallback(index => {
    const currentColumn = currentMeasureColumns.get(index);
    const options = getAggregationOptions(index);
    if (options.length <= 1 || currentColumn.aggregation === AggregationTypes.DELEGATE || isMagicMeasure(currentColumn)) {
      return I18n.text('report-customization-components.customization-components.relational-report.singleAggregationOption');
    }
    return undefined;
  }, [currentMeasureColumns, getAggregationOptions]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  if (fullTableAndPropertyMetaError) {
    return /*#__PURE__*/_jsx(ErrorAlert, {
      errorMessage: I18n.text('report-customization-components.customization-components.relational-report.metric-fetch-error'),
      error: fullTableAndPropertyMetaError
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(FormControl, {
      children: /*#__PURE__*/_jsx(ErrorAlert, {
        errorMessage: I18n.text('report-customization-components.customization-components.relational-report.metric-update-error'),
        error: error
      })
    });
  }
  return /*#__PURE__*/_jsx(MetricEditor, {
    reportMetrics: reportMetrics,
    onAddMetric: handleAddMetric,
    onDeleteMetric: handleDeleteMetric,
    onMetricChange: handleMetricChange,
    propertyOptions: availablePropertiesOptions,
    getAggregationOptions: getAggregationOptions,
    isDeleteDisabled: isDeleteDisabled,
    isAggregationDisabled: isAggregationDisabled,
    getAggregationDisabledReason: getAggregationDisabledReason,
    addMetricDisabledReason: getAddMetricDisabledReason(),
    hideAddButton: allAvailableColumns.size === 0 || hideAddButton,
    "data-test-id": props['data-test-id'],
    buttonUse: props.buttonUse
  });
};
export default RelationalReportMetricsEditorDropdown;